import { useState, useEffect } from 'react';
import {
  useListAlerts,
  useGetAlertSettings,
  useCreateAlert,
  useUpdateAlert,
  useDeleteAlert,
  useUpdateAlertSettings,
} from '@workspace/api-client-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformBadge } from '@/components/platform-badge';
import { Spinner } from '@/components/ui/spinner';
import { Bell, Plus, Trash2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

export default function AlertsPage() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    marketQuestion: '',
    platform: 'polymarket',
    targetPrice: '',
    direction: 'above' as 'above' | 'below',
    deliveryChannels: ['in-app'] as string[],
  });

  const { data: alerts, isLoading: alertsLoading } = useListAlerts();
  const { data: settings, isLoading: settingsLoading } = useGetAlertSettings();
  const createAlert = useCreateAlert();
  const updateAlert = useUpdateAlert();
  const deleteAlert = useDeleteAlert();
  const updateSettings = useUpdateAlertSettings();

  const [settingsForm, setSettingsForm] = useState({
    emailEnabled: false,
    emailAddress: '',
    pushEnabled: false,
    smsEnabled: false,
    smsPhone: '',
  });

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        emailEnabled: settings.emailEnabled ?? false,
        emailAddress: settings.emailAddress ?? '',
        pushEnabled: settings.pushEnabled ?? false,
        smsEnabled: settings.smsEnabled ?? false,
        smsPhone: settings.smsPhone ?? '',
      });
    }
  }, [settings]);

  const handleSubmitAlert = (e: React.FormEvent) => {
    e.preventDefault();
    const marketId = `manual-${Date.now()}`;

    createAlert.mutate(
      {
        data: {
          marketId,
          marketQuestion: formData.marketQuestion,
          platform: formData.platform as any,
          targetPrice: Number(formData.targetPrice),
          direction: formData.direction,
          deliveryChannels: formData.deliveryChannels,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['alerts'] });
          trackEvent(FUNNEL_EVENTS.PRICE_ALERT_CREATED, {
            marketId,
            platform: formData.platform,
            direction: formData.direction,
          });
          setFormData({
            marketQuestion: '',
            platform: 'polymarket',
            targetPrice: '',
            direction: 'above',
            deliveryChannels: ['in-app'],
          });
          setShowAddForm(false);
        },
      }
    );
  };

  const handleToggleActive = (alert: { id: number; marketId: string; platform: string; isActive: boolean }) => {
    updateAlert.mutate(
      {
        id: alert.id,
        data: { isActive: !alert.isActive },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['alerts'] });
          trackEvent(FUNNEL_EVENTS.PRICE_ALERT_TOGGLED, {
            marketId: alert.marketId,
            platform: alert.platform,
            enabled: !alert.isActive,
          });
        },
      }
    );
  };

  const handleDeleteAlert = (alert: { id: number; marketId: string; platform: string }) => {
    if (confirm('Are you sure you want to delete this alert?')) {
      deleteAlert.mutate(
        { id: alert.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['alerts'] });
            trackEvent(FUNNEL_EVENTS.PRICE_ALERT_DELETED, {
              marketId: alert.marketId,
              platform: alert.platform,
            });
          },
        }
      );
    }
  };

  const handleUpdateSettings = () => {
    updateSettings.mutate(
      { data: settingsForm },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['alert-settings'] });
        },
      }
    );
  };

  const toggleChannel = (channel: string) => {
    setFormData({
      ...formData,
      deliveryChannels: formData.deliveryChannels.includes(channel)
        ? formData.deliveryChannels.filter((c) => c !== channel)
        : [...formData.deliveryChannels, channel],
    });
  };

  if (alertsLoading || settingsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Alerts</h1>
          <p className="text-muted-foreground mt-1">
            Get notified when markets hit your target prices
          </p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Alert
        </Button>
      </div>

      {/* Add Alert Form */}
      <Collapsible open={showAddForm} onOpenChange={setShowAddForm}>
        <CollapsibleContent>
          <Card>
            <CardHeader>
              <CardTitle>Create Alert</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitAlert} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="marketQuestion">Market Question</Label>
                    <Input
                      id="marketQuestion"
                      value={formData.marketQuestion}
                      onChange={(e) =>
                        setFormData({ ...formData, marketQuestion: e.target.value })
                      }
                      placeholder="Will Bitcoin hit $100k in 2024?"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="platform">Platform</Label>
                    <Select
                      value={formData.platform}
                      onValueChange={(value) =>
                        setFormData({ ...formData, platform: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="polymarket">Polymarket</SelectItem>
                        <SelectItem value="kalshi">Kalshi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="targetPrice">Target Price (cents)</Label>
                    <Input
                      id="targetPrice"
                      type="number"
                      step="0.01"
                      value={formData.targetPrice}
                      onChange={(e) =>
                        setFormData({ ...formData, targetPrice: e.target.value })
                      }
                      placeholder="75"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="direction">Direction</Label>
                    <Select
                      value={formData.direction}
                      onValueChange={(value: 'above' | 'below') =>
                        setFormData({ ...formData, direction: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="above">Above</SelectItem>
                        <SelectItem value="below">Below</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Delivery Channels</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="in-app"
                        checked={formData.deliveryChannels.includes('in-app')}
                        onCheckedChange={() => toggleChannel('in-app')}
                      />
                      <label htmlFor="in-app" className="text-sm cursor-pointer">
                        In-App Notifications
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="email"
                        checked={formData.deliveryChannels.includes('email')}
                        onCheckedChange={() => toggleChannel('email')}
                      />
                      <label htmlFor="email" className="text-sm cursor-pointer">
                        Email
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="push"
                        checked={formData.deliveryChannels.includes('push')}
                        onCheckedChange={() => toggleChannel('push')}
                      />
                      <label htmlFor="push" className="text-sm cursor-pointer">
                        Push Notifications
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sms"
                        checked={formData.deliveryChannels.includes('sms')}
                        onCheckedChange={() => toggleChannel('sms')}
                      />
                      <label htmlFor="sms" className="text-sm cursor-pointer">
                        SMS
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={createAlert.isPending}>
                    {createAlert.isPending ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Alert'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* Alerts List */}
      <Card>
        <CardHeader>
          <CardTitle>My Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          {alerts && alerts.alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <PlatformBadge platform={alert.platform} />
                      {alert.triggered && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                          <Check className="h-3 w-3" />
                          Triggered
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium">{alert.marketQuestion}</p>
                    <p className="text-sm text-muted-foreground">
                      Alert when price goes {alert.direction} {alert.targetPrice}¢
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={alert.isActive}
                      onCheckedChange={() => handleToggleActive(alert)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAlert(alert)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No alerts set up yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alert Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Alert Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-toggle">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive alerts via email
                </p>
              </div>
              <Switch
                id="email-toggle"
                checked={settingsForm.emailEnabled}
                onCheckedChange={(checked) =>
                  setSettingsForm({ ...settingsForm, emailEnabled: checked })
                }
              />
            </div>

            {settingsForm.emailEnabled && (
              <div className="space-y-2 pl-4">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={settingsForm.emailAddress}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, emailAddress: e.target.value })
                  }
                  placeholder="you@example.com"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="push-toggle">Push Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive browser push notifications
              </p>
            </div>
            <Switch
              id="push-toggle"
              checked={settingsForm.pushEnabled}
              onCheckedChange={(checked) =>
                setSettingsForm({ ...settingsForm, pushEnabled: checked })
              }
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="sms-toggle">SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive alerts via text message
                </p>
              </div>
              <Switch
                id="sms-toggle"
                checked={settingsForm.smsEnabled}
                onCheckedChange={(checked) =>
                  setSettingsForm({ ...settingsForm, smsEnabled: checked })
                }
              />
            </div>

            {settingsForm.smsEnabled && (
              <div className="space-y-2 pl-4">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={settingsForm.smsPhone}
                  onChange={(e) =>
                    setSettingsForm({ ...settingsForm, smsPhone: e.target.value })
                  }
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            )}
          </div>

          <Button onClick={handleUpdateSettings} disabled={updateSettings.isPending}>
            {updateSettings.isPending ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
